import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';

import { CenterAlignedCell, DollarValueCell } from 'components/Table';
import { useIsLoggedIn } from 'hooks/useIsLoggedIn';
import ProsforaAuctionVolume from 'types/api/ProsforaAuctionVolume';
import { DEFAULT_DATE_TIME_FORMAT, formatDate } from 'hooks/useFormattedDate';

import DisabledChevronCell from '../components/DisabledChevronCell/DisabledChevronCell';

const useColumns = () => {
  const { t } = useTranslation();
  const isLoggedIn = useIsLoggedIn();

  return useMemo(() => {
    const columnHelper = createColumnHelper<ProsforaAuctionVolume>();

    const columns: ColumnDef<ProsforaAuctionVolume, any>[] = [];

    columns.push(
      columnHelper.accessor('issuance_number', {
        cell: CenterAlignedCell,
        header: t('pages.prosforaProject.volumes.number'),
        size: 10,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor('issuance_year', {
        cell: CenterAlignedCell,
        header: t('pages.prosforaProject.volumes.issuanceYear'),
        size: 50,
        meta: {
          headerClassName: 'center',
        },
      }),
      columnHelper.accessor('issuance_name', {
        header: t('pages.prosforaProject.volumes.issuanceName'),
        size: 100,
      }),
      columnHelper.accessor('total_volume', {
        header: t('pages.prosforaProject.volumes.totalVolume'),
        cell: CenterAlignedCell,
        size: 50,
        meta: {
          headerClassName: 'center',
        },
      }),
    );

    if (isLoggedIn) {
      columns.push(
        columnHelper.accessor((row) => formatDate(row.last_bid_date, DEFAULT_DATE_TIME_FORMAT), {
          id: 'last_bid_date',
          header: t('pages.prosforaProject.volumes.lastBidDate'),
          cell: CenterAlignedCell,
          size: 50,
          meta: {
            headerClassName: 'center',
          },
        }),
        columnHelper.accessor('last_bid_price', {
          header: t('pages.prosforaProject.volumes.lastBidPrice'),
          cell: DollarValueCell,
          size: 50,
          meta: {
            headerClassName: 'center',
          },
        }),
      );
    }

    columns.push(
      columnHelper.accessor('id', {
        header: '',
        size: 100,
        cell: DisabledChevronCell,
      }),
    );

    return columns;
  }, [isLoggedIn, t]);
};

export default useColumns;
